<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    @change="editSingleSettingHandler"
    :has-buttons="parcelsProvider === 'system' && systemProviderProps.datasource ? true : false"
    has-validation
    :is-form-fetched="isFormFetched"
    @save="save"
  >
    <template #systemProviderDatasource.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="systemProviderProps.datasource" @change="clearColumns" />
      </v-col>
    </template>
    <template #attribute_community.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_community"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
    <template #attribute_region.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_region"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
    <template #attribute_number.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="systemProviderProps.settings.attribute_number"
          @change="selectedSystemProviderPropsChanged = true"
        />
      </v-col>
    </template>
    <template #uldkAttributes.input="data">
      <v-col cols="12">
        <parcels-settings-uldk-attributes v-bind="data" />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import ParcelsSettingsUldkAttributes from '@/components/ParcelsSettingsUldkAttributes';

export default {
  name: 'ParcelsSettings',
  components: {
    AdminWrapper,
    DataInput,
    ParcelsSettingsUldkAttributes,
  },
  data: () => ({
    isFormFetched: false,
    selectedSystemProviderPropsChanged: false,
    systemProviderProps: {
      settings: {
        attribute_community: '',
        attribute_region: '',
        attribute_number: '',
      },
      datasource: '',
    },
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@PARCELS.verbose_name'),
    dataSources: get('layers/metadata'),
    isModuleEnabled: get('admin/settingsValues@parcels_module_enabled'),
    parcelsProvider: get('admin/settingsValues@parcels_provider'),
    dataSourceSettingsName() {
      return 'parcels_datasource';
    },
    sortedPolygonDataSources() {
      const polygonDataSources = this.$_objectToArray(this.dataSources).filter(
        dataSource => ['polygon', 'multipolygon'].includes(dataSource.geometry_type) && dataSource.scope === 'core'
      );
      return this.$_sortObjectsByAttribute([...polygonDataSources], 'verbose_name');
    },
    sortedSystemProviderDatasourceAttributes() {
      const systemProviderDatasourceAttributes = this.systemProviderDatasource.attributes_schema.attributes;
      return this.$_sortObjectsByAttribute([...systemProviderDatasourceAttributes], 'verbose_name');
    },
    systemProviderDatasource() {
      return this.dataSources[this.systemProviderProps.datasource];
    },
    moduleEnabler() {
      return {
        name: 'parcels_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      let inputs = [
        {
          name: 'parcels_provider',
          translationPath: 'admin.parcelsProvider',
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          dataType: {
            name: 'select',
          },
          items: [
            { text: this.$i18n.t('admin.uldk'), value: 'uldk' },
            { text: this.$i18n.t('admin.system'), value: 'system' },
          ],
        },
        ...(this.parcelsProvider === 'uldk' ? this.uldkInputs : []),
      ];
      if (this.parcelsProvider === 'system') {
        const systemInputs = [
          {
            name: 'systemProviderDatasource',
            translationPath: 'admin.systemProviderDatasource',
            subtitle: 'admin.systemProviderDatasourceSubtitle',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'select',
            },
            align: 'start',
            itemValue: 'name',
            items: this.sortedPolygonDataSources,
            itemText: 'verbose_name',
          },
        ];
        if (this.systemProviderProps.datasource) {
          const columns = ['attribute_community', 'attribute_region', 'attribute_number'];
          for (const column of columns) {
            const otherColumns = columns.filter(col => col != column);
            const otherColumnsValues = otherColumns.map(oC => this.systemProviderProps.settings[oC]);
            const items = this.sortedSystemProviderDatasourceAttributes.filter(
              att => !otherColumnsValues.includes(att.name)
            );
            systemInputs.push({
              name: column,
              translationPath: `admin.${column}`,
              subtitle: `admin.${column}Subtitle`,
              rules: [v => !!v || v == '0' || 'rules.required'],
              align: 'start',
              dataType: {
                name: 'select',
              },
              items,
              itemText: 'verbose_name',
              itemValue: 'name',
            });
          }
        }
        return inputs.concat(systemInputs);
      }
      return inputs;
    },
    uldkInputs() {
      return [
        {
          name: 'uldkAttributes',
          translationPath: 'admin.parcelsUldk.attributesMapping',
          isNamedSlot: false,
        },
      ];
    },
  },
  methods: {
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    editSingleSetting: call('admin/editSingleSetting'),
    getDataSources: call('layers/getLayersMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    clearColumns() {
      this.systemProviderProps.settings.attribute_community = '';
      this.systemProviderProps.settings.attribute_region = '';
      this.systemProviderProps.settings.attribute_number = '';
      this.selectedSystemProviderPropsChanged = true;
    },
    async editSingleSettingHandler(value, name) {
      if (name === 'parcels_provider') {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      } else {
        return;
      }
    },
    async updateSelectedDataSource() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.systemProviderProps = {
          datasource,
          settings,
        };
        this.selectedSystemProviderPropsChanged = false;
      } catch {
        this.systemProviderProps = {
          settings: {
            attribute_community: '',
            attribute_region: '',
            attribute_number: '',
          },
          datasource: '',
        };
        this.selectedSystemProviderPropsChanged = false;
      }
    },
    async save() {
      if (!this.selectedSystemProviderPropsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.systemProviderProps };
      try {
        await this.editDatasourceSettings(payload);
        this.selectedSystemProviderPropsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        console.log('  error', error);
      }
    },
    async init() {
      await Promise.all([this.getModulesMapping(), this.getDataSources({ with_attributes_schema: true })]);
      if (this.parcelsProvider === 'system' && this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFormFetched = true;
    },
  },
  watch: {
    parcelsProvider(nV) {
      if (nV === 'system' && this.isModuleEnabled) {
        this.updateSelectedDataSource();
      }
    },
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
